import React from "react";
import { Link } from "react-router-dom";

interface RoleCard {
  name: string;
  badge: string;
  badgeColor: string;
  description: string;
  canDo: string[];
  cannotDo: string[];
  notes?: string;
}

const ROLES: RoleCard[] = [
  {
    name: "Account Owner",
    badge: "Owner",
    badgeColor: "bg-purple-100 text-purple-800",
    description: "The primary account holder. Full control over every setting, user, and billing item. Typically the person who signed up.",
    canDo: [
      "Everything admins can do",
      "Manage billing and subscription plan",
      "Transfer ownership to another user",
      "Delete the account",
      "View all compliance and security reports",
    ],
    cannotDo: [],
    notes: "There is one Owner per account. If you need to change the owner, contact Velaro support.",
  },
  {
    name: "Admin",
    badge: "Admin",
    badgeColor: "bg-blue-100 text-blue-800",
    description: "Full management of settings, users, workflows, and integrations. Admins use this portal to configure the platform for their team.",
    canDo: [
      "Add, edit, and deactivate users",
      "Create and manage teams",
      "Build and publish workflows and rules",
      "Configure integrations (CRM, calendar, SMS, etc.)",
      "Manage the knowledge base",
      "Run and export reports",
      "View activity log and agent login history",
      "Configure chatbot and AI settings",
      "Manage email and IVR campaigns",
    ],
    cannotDo: [
      "Change billing or subscription plan (Owner only)",
      "Transfer account ownership",
    ],
  },
  {
    name: "Agent",
    badge: "Agent",
    badgeColor: "bg-green-100 text-green-800",
    description: "Front-line team members who handle live chats, voice calls, and messages in the agent workspace. They do not have access to this admin portal.",
    canDo: [
      "Accept and manage live chat/voice conversations",
      "View and respond to messages",
      "Access canned responses and knowledge base articles",
      "Set their own availability status",
      "View their own performance stats",
    ],
    cannotDo: [
      "Access this admin portal",
      "Create or edit workflows, rules, or bots",
      "Add or remove other users",
      "View other agents' login history",
      "Change account settings",
    ],
    notes: "Agents log in to the agent workspace, not this admin portal. An agent can be promoted to admin by an Account Owner or Admin.",
  },
  {
    name: "Team Manager",
    badge: "Team Manager",
    badgeColor: "bg-amber-100 text-amber-800",
    description: "A role for supervisors who need read-access to team-scoped reports without full admin access.",
    canDo: [
      "View reports filtered to their assigned teams",
      "View agent login history for their team members",
      "Monitor queue and availability for their teams",
    ],
    cannotDo: [
      "Modify account-wide settings",
      "Add or remove users across the account",
      "View data outside their assigned teams",
    ],
    notes: "Team Managers get read-access to team-scoped reports and can monitor queue and availability for their assigned teams.",
  },
  {
    name: "Billing Contact",
    badge: "Billing",
    badgeColor: "bg-slate-100 text-slate-700",
    description: "Receives billing notifications and invoices. Does not have login access to the platform.",
    canDo: [
      "Receive invoices and payment receipts via email",
      "Be listed as the billing contact for the account",
    ],
    cannotDo: [
      "Log in to the admin portal or agent workspace",
      "Change any settings",
    ],
    notes: "A billing contact is just an email address on file. To give a billing person actual access to manage the subscription, make them an Account Owner.",
  },
];

function RoleRow({ role }: { role: RoleCard }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">{role.name}</h2>
          <p className="text-sm text-slate-500 mt-1">{role.description}</p>
        </div>
        <span className={`ml-4 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${role.badgeColor}`}>
          {role.badge}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Can do</p>
          <ul className="space-y-1">
            {role.canDo.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                <i className="fa fa-check text-green-500 mt-0.5 flex-shrink-0 text-xs" />
                {item}
              </li>
            ))}
            {role.canDo.length === 0 && (
              <li className="text-sm text-slate-400 italic">No restrictions</li>
            )}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Cannot do</p>
          <ul className="space-y-1">
            {role.cannotDo.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                <i className="fa fa-times text-slate-300 mt-0.5 flex-shrink-0 text-xs" />
                {item}
              </li>
            ))}
            {role.cannotDo.length === 0 && (
              <li className="text-sm text-slate-400 italic">No restrictions</li>
            )}
          </ul>
        </div>
      </div>

      {role.notes && (
        <div className="mt-4 p-3 bg-slate-50 rounded border border-slate-100 text-xs text-slate-600">
          <i className="fa fa-circle-info text-blue-400 mr-1.5" />
          {role.notes}
        </div>
      )}
    </div>
  );
}

export default function UserRoles() {
  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <p className="text-sm text-slate-500">
          All user accounts in Velaro have a role that controls what they can see and do.
          Roles are account-wide — they are not team-scoped (except the Team Manager role).
        </p>
      </div>

      {/* Role hierarchy summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-slate-700">
        <p className="font-medium text-blue-800 mb-2">Role hierarchy (highest to lowest access)</p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Owner</span>
          <i className="fa fa-chevron-right text-slate-300 text-xs" />
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Admin</span>
          <i className="fa fa-chevron-right text-slate-300 text-xs" />
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Team Manager</span>
          <i className="fa fa-chevron-right text-slate-300 text-xs" />
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Agent</span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Higher roles inherit all capabilities of lower roles. An Admin can do everything an Agent can do, plus more.
        </p>
      </div>

      {ROLES.map((role) => (
        <RoleRow key={role.name} role={role} />
      ))}

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
        <p className="font-medium mb-1">To change a user's role</p>
        <p>Go to <Link to="/users" className="text-blue-600 hover:underline">Settings → Users</Link>, open the user's profile, and update their role. Only Account Owners and Admins can change roles. You cannot demote the Account Owner.</p>
      </div>
    </div>
  );
}
